import { Box, HStack, Icon, Stack, Text } from '@chakra-ui/react';
import { LuCircleCheck } from 'react-icons/lu';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { DashboardResponse } from '@/lib/api';

type Aging = DashboardResponse['financiero']['aging'];

// Whole quetzales, to reconcile with the rounded "Saldo en mora" KPI it mirrors.
const fmtGTQ = new Intl.NumberFormat('es-GT', {
  style: 'currency',
  currency: 'GTQ',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

// Round shares to whole percents that sum to exactly 100 (largest-remainder),
// so the legend never reads 99% or 101%.
function wholePercents(values: number[], total: number): number[] {
  if (total <= 0) return values.map(() => 0);
  const raw = values.map((v) => (v / total) * 100);
  const out = raw.map((r) => Math.floor(r));
  let left = 100 - out.reduce((s, n) => s + n, 0);
  const byFrac = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => b.frac - a.frac);
  for (let k = 0; k < byFrac.length && left > 0; k += 1, left -= 1) {
    out[byFrac[k].i] += 1;
  }
  return out;
}

// Severity ramp (amber → deep red). Literal hex so it never depends on which
// Chakra color palettes are registered.
const BUCKETS: { key: keyof Aging; label: string; color: string }[] = [
  { key: 'd1_30', label: '1–30 días', color: '#FBBF24' },
  { key: 'd31_60', label: '31–60 días', color: '#FB923C' },
  { key: 'd61_90', label: '61–90 días', color: '#F87171' },
  { key: 'd90plus', label: '+90 días', color: '#DC2626' },
];

export function AgingDonut({ aging }: { aging: Aging }) {
  const data = BUCKETS.map((b) => ({ ...b, value: Number(aging[b.key]) }));
  const total = data.reduce((s, d) => s + d.value, 0);
  const pcts = wholePercents(
    data.map((d) => d.value),
    total,
  );

  return (
    <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '5' }} h="320px">
      <Text fontSize="sm" fontWeight="600" mb="2">
        Antigüedad de mora
      </Text>
      {total <= 0 ? (
        <Stack align="center" justify="center" h="240px" gap="2" color="fg.muted">
          <Icon as={LuCircleCheck} boxSize="8" color="green.500" />
          <Text fontSize="sm">Cartera al día — sin cuotas en mora.</Text>
        </Stack>
      ) : (
        <HStack align="center" gap="4" h="240px">
          <Box w="160px" h="100%">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="label" innerRadius={48} outerRadius={72} paddingAngle={2} stroke="none">
                  {data.map((d) => (
                    <Cell key={d.key} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [fmtGTQ.format(Number(value ?? 0)), name as string]}
                  contentStyle={{
                    background: 'var(--chakra-colors-bg-surface)',
                    border: '1px solid var(--chakra-colors-border)',
                    borderRadius: '6px',
                    fontSize: '13px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Stack gap="2" flex="1">
            {data.map((d, i) => (
              <HStack key={d.key} justify="space-between" gap="3">
                <HStack gap="2">
                  <Box w="2.5" h="2.5" rounded="full" style={{ background: d.color }} />
                  <Text fontSize="xs" color="fg.muted">
                    {d.label}
                  </Text>
                </HStack>
                <Text fontSize="xs" fontWeight="600">
                  {fmtGTQ.format(d.value)}
                  <Text as="span" color="fg.muted" fontWeight="400" ml="1">
                    · {pcts[i]}%
                  </Text>
                </Text>
              </HStack>
            ))}
          </Stack>
        </HStack>
      )}
    </Box>
  );
}
